import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { coerceHttpParams } from 'src/app/shared/flight-log-api/shared/create-request-options';
import { AppState } from 'src/store';
import {
  DeleteDrugAlcoholRecord,
  UpsertDrugAlcoholRecord,
  UpsertDrugAlcoholRecords,
} from 'src/store/drug-alcohol-record/drug-alcohol-record.actions';
import { DrugAlcoholRecordEntity } from 'src/store/drug-alcohol-record/drug-alcohol-record.model';
import { assertNotNullOrWhitespace } from 'src/util/values';
import { DrugAlcoholRecordViewModel } from 'src/view-models/drug-alcohol-record-view-model';
import { FLIGHT_LOG_API_CONFIG, FlightLogApiConfiguration } from '../flight-log-api-configuration';
import { CollectionResult } from '../flight-logs/flight-log-client-model-helpers';
import { RecordTypeHttpClient } from '../record-types/record-types-client.service';
import { ReportTypeHttpClient } from '../report-types/report-types-client.service';
import { TestTypeHttpClient } from '../test-types/test-types-client.service';

export type DrugAlcoholRecords<T extends DrugAlcoholRecordEntity = DrugAlcoholRecordEntity> =
  CollectionResult<T>;

@Injectable({ providedIn: 'root' })
export class DrugAlcoholRecordHttpClient {
  constructor(
    @Inject(FLIGHT_LOG_API_CONFIG) private config: FlightLogApiConfiguration,
    private http: HttpClient,
    private _store: Store<AppState>,
    private _recordType: RecordTypeHttpClient,
    private _reportType: ReportTypeHttpClient,
    private _testType: TestTypeHttpClient,
  ) {}

  public create(drugAlcoholRecord: DrugAlcoholRecordEntity, pilotId: string): Observable<any> {
    return this.http.post(this._url(`pilots/${pilotId}/drugAlcoholRecords`), drugAlcoholRecord);
  }

  public update(drugAlcoholRecord: DrugAlcoholRecordEntity, pilotId: string): Observable<any> {
    return this.http.put(this._url(`pilots/${pilotId}/drugAlcoholRecords`), drugAlcoholRecord).pipe(
      tap(() => {
        this._store.dispatch(new UpsertDrugAlcoholRecord({ drugAlcoholRecord }));
      }),
    );
  }

  public delete(drugAlcoholRecordId: string): Observable<any> {
    assertNotNullOrWhitespace(drugAlcoholRecordId, 'drugAlcoholRecordId');
    return this.http
      .delete(this._url(`drugAlcoholRecords/${drugAlcoholRecordId}/`))
      .pipe(
        tap(() => this._store.dispatch(new DeleteDrugAlcoholRecord({ id: drugAlcoholRecordId }))),
      );
  }

  public getById(id: string): Observable<DrugAlcoholRecords<DrugAlcoholRecordViewModel>> {
    assertNotNullOrWhitespace(id, 'drugAlcoholRecordId');
    return this.http.get<DrugAlcoholRecords>(this._url(`drugAlcoholRecords/${id}`)).pipe(
      tap(({ results: drugAlcoholRecords }) => {
        this._store.dispatch(new UpsertDrugAlcoholRecords({ drugAlcoholRecords }));
      }),
      map((res) => {
        return {
          results: res.results.map(
            (r) =>
              new DrugAlcoholRecordViewModel(r, this._recordType, this._reportType, this._testType),
          ),
          totalCount: res.totalCount,
        };
      }),
    );
  }

  public getByPilotId(params: {
    pilotId: string;
    searchTerm?: string;
    includeDeleted?: boolean;
    take?: number;
    skip?: number;
  }): Observable<DrugAlcoholRecords<DrugAlcoholRecordViewModel>> {
    return this.http
      .get<DrugAlcoholRecords>(this._url(`pilots/${params.pilotId}/drugAlcoholRecords/`), {
        params: coerceHttpParams({
          ...params,
        }),
      })
      .pipe(
        tap(({ results: drugAlcoholRecords }) => {
          this._store.dispatch(new UpsertDrugAlcoholRecords({ drugAlcoholRecords }));
        }),
        map((res) => {
          return {
            results: res.results.map(
              (r) =>
                new DrugAlcoholRecordViewModel(
                  r,
                  this._recordType,
                  this._reportType,
                  this._testType,
                ),
            ),
            totalCount: res.totalCount,
          };
        }),
      );
  }

  private _url(path: string) {
    return this.config.baseUri + '/api/' + path;
  }
}
